import { createContext, useContext, useEffect, useMemo, useReducer, useState, type ReactNode } from 'react';
import { useRadarLocale } from '@/i18n/RadarLocaleContext';
import type { RadarLocale } from '@/i18n/radarLanding';
import type { RadarWorkspaceBundle } from './types';
import { radarApi } from './radarApi';
import { radarDisplayTexts } from './radarDisplayTexts';

const Context = createContext({ d: (value?: string | null) => value || '' });
export const useRadarDisplay = () => useContext(Context);
const cacheKey = (locale: RadarLocale, source: string) => JSON.stringify([locale, source]);

// Mounted with an owner/workspace key. Nothing is stored in localStorage or written to research records.
export function RadarDisplayProvider({ translations = {}, token, bundle, workspaceId: explicitWorkspaceId, sourceTexts, section = 'brief', children }: {
  translations?: Partial<Record<RadarLocale, Record<string, string>>>;
  token?: string; bundle?: RadarWorkspaceBundle; workspaceId?: string; sourceTexts?: readonly string[];
  section?: 'brief' | 'discover' | 'watch' | 'act'; children: ReactNode;
}) {
  const { locale, t } = useRadarLocale();
  const [original, setOriginal] = useState(false);
  const [retry, retryTranslation] = useReducer(n => n + 1, 0);
  const [, redraw] = useReducer(n => n + 1, 0);
  const workspaceId = explicitWorkspaceId ?? bundle?.workspace.id;
  const memory = useMemo(() => ({ cache: new Map<string, string>(), pending: new Map<string, Promise<void>>() }), [workspaceId]);
  // Expo has persisted prose without a full workspace bundle; keep the same request bounds.
  const sourceKey = JSON.stringify([...new Set(sourceTexts ?? (bundle ? radarDisplayTexts(bundle, section) : []))]
    .filter(text => text.trim() && text.length <= 2400));
  const sources: string[] = useMemo(() => JSON.parse(sourceKey), [sourceKey]);
  const identity = JSON.stringify([workspaceId, locale, sourceKey, retry]);
  const [status, setStatus] = useState({ identity: '', value: 'idle' });

  useEffect(() => {
    if (!token || !workspaceId || !sources.length) return;
    let disposed = false;
    setStatus({ identity, value: 'loading' });
    async function translate() {
      const missing = sources.filter(source => !memory.cache.has(cacheKey(locale, source)));
      for (let offset = 0; offset < missing.length;) {
        const batch: string[] = [];
        let size = 0;
        while (offset < missing.length && batch.length < 24 && size + missing[offset].length <= 7000) {
          const source = missing[offset++]; batch.push(source); size += source.length;
        }
        if (disposed) return;
        const requestKey = JSON.stringify([locale, batch]);
        let pending = memory.pending.get(requestKey);
        if (!pending) {
          pending = radarApi.translate(token!, workspaceId!, locale, batch).then(result => {
            if (result.translations.length !== batch.length || result.translations.some(text => !text.trim())) throw new Error('Incomplete translation');
            batch.forEach((source, index) => memory.cache.set(cacheKey(locale, source), result.translations[index]));
            while (memory.cache.size > 2000) memory.cache.delete(memory.cache.keys().next().value!);
          }).finally(() => { memory.pending.delete(requestKey); });
          memory.pending.set(requestKey, pending);
        }
        await pending;
        if (!disposed) redraw();
      }
      if (!disposed) setStatus({ identity, value: 'ready' });
    }
    translate().catch(() => { if (!disposed) setStatus({ identity, value: 'error' }); });
    return () => { disposed = true; };
  }, [token, workspaceId, locale, sources, memory, identity]);

  // Only current persisted strings may resolve from the remote cache (draft edits stay raw).
  const allowed = new Set(sources);
  const injected = translations[locale];
  const translated = !!injected || sources.some(source => memory.cache.has(cacheKey(locale, source)));
  const d = (value?: string | null) => !value ? '' : original ? value
    : injected?.[value] || (allowed.has(value) ? memory.cache.get(cacheKey(locale, value)) : undefined) || value;
  const current = status.identity === identity ? status.value : 'idle';
  const message = current === 'error' ? 'Translation is unavailable. Original text remains available.'
    : current === 'loading' ? 'Translating… Original text remains available.'
    : translated ? 'Translated for reference; check the original when needed.' : 'Research content is shown in its original language.';
  return <Context.Provider value={{ d }}>
    <div className="radar-translation-status" aria-live="polite">
      <span>{t(message)}</span>
      {current === 'error' && <button type="button" onClick={retryTranslation}>{t('Retry translation')}</button>}
      {translated && <button type="button" onClick={() => setOriginal(value => !value)}>{t(original ? 'Show translation' : 'Show original')}</button>}
    </div>
    {children}
  </Context.Provider>;
}
